import { Layout, Menu, Button, Typography, Grid, Drawer, Dropdown, Avatar, Tooltip, Badge } from "antd";
import type { MenuProps } from "antd";
import {
  ApartmentOutlined,
  BarChartOutlined,
  BookOutlined,
  DisconnectOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  CommentOutlined,
  DownOutlined,
  HomeOutlined,
  EditOutlined,
  FileDoneOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  NotificationOutlined,
  RiseOutlined,
  SafetyOutlined,
  SendOutlined,
  SolutionOutlined,
  TagsOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserDeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAuth } from "../app/auth.tsx";
import { usePendingExcuseCount } from "../app/excuses.ts";
import { brand } from "../app/theme.ts";

const { Header, Sider, Content } = Layout;

const SIDER_WIDTH = 240;
const SIDER_COLLAPSED_WIDTH = 64;
const COLLAPSED_KEY = "portal.sider.collapsed";

type NavItem = { key: string; icon: React.ReactNode; label: string; extra?: React.ReactNode };

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function initials(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

export function AppLayout() {
  const { t } = useTranslation();
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const canExcuse = hasPermission("attendance.excuse");
  const pendingExcuses = usePendingExcuseCount(canExcuse).data ?? 0;

  const toggleCollapsed = () =>
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // Storage can be unavailable (private mode); the choice then lasts for this visit only.
      }
      return next;
    });

  const people: NavItem[] = [
    ...(hasPermission("people.read")
      ? [
          { key: "/families", icon: <TeamOutlined />, label: t("nav.families") },
          { key: "/teachers", icon: <SolutionOutlined />, label: t("nav.teachers") },
          { key: "/staff", icon: <IdcardOutlined />, label: t("nav.staff") },
          { key: "/leaving-reasons", icon: <UserDeleteOutlined />, label: t("nav.leavingReasons") },
          { key: "/telegram/unreachable", icon: <DisconnectOutlined />, label: t("nav.telegramReach") },
        ]
      : []),
  ];
  const academic: NavItem[] = [
    ...(hasPermission("academic.read")
      ? [
          { key: "/academic/classes", icon: <ApartmentOutlined />, label: t("nav.classes") },
          { key: "/academic/subjects", icon: <BookOutlined />, label: t("nav.subjectsCourses") },
          { key: "/academic/years", icon: <CalendarOutlined />, label: t("nav.academicYears") },
        ]
      : []),
    ...(hasPermission("people.manage") && hasPermission("academic.read")
      ? [{ key: "/rollover", icon: <RiseOutlined />, label: t("nav.rollover") }]
      : []),
    ...(canExcuse
      ? [
          {
            key: "/attendance/excuse-requests",
            icon: <FileDoneOutlined />,
            label: t("nav.excuseRequests"),
            extra: <Badge count={pendingExcuses} size="small" title={t("nav.pendingExcuseRequests", { count: pendingExcuses })} />,
          },
        ]
      : []),
    ...(hasPermission("grading.readAll") ? [{ key: "/grading/settings", icon: <EditOutlined />, label: t("nav.gradingSettings") }] : []),
    ...(hasPermission("grading.read") ? [{ key: "/grading/exams", icon: <TrophyOutlined />, label: t("nav.exams") }] : []),
  ];
  const finance: NavItem[] = [
    ...(hasPermission("billing.read") ? [{ key: "/billing/rules", icon: <TagsOutlined />, label: t("nav.billingRules") }] : []),
  ];
  const reports: NavItem[] = [
    ...(hasPermission("reports.read") ? [{ key: "/reports/attendance", icon: <BarChartOutlined />, label: t("nav.attendanceReport") }] : []),
    ...(hasPermission("attendance.readAll") ? [{ key: "/attendance/unmarked", icon: <ExclamationCircleOutlined />, label: t("nav.unmarkedLessons") }] : []),
  ];
  const communication: NavItem[] = [
    ...(hasPermission("announcements.read") ? [{ key: "/announcements", icon: <NotificationOutlined />, label: t("nav.announcements") }] : []),
    ...(hasPermission("teacherMessages.send") || hasPermission("teacherMessages.read")
      ? [{ key: "/teacher-messages", icon: <CommentOutlined />, label: t("nav.teacherMessages") }]
      : []),
  ];
  const administration: NavItem[] = [
    ...(hasPermission("identity.manage") ? [{ key: "/users", icon: <UserOutlined />, label: t("nav.users") }] : []),
    ...(hasPermission("notifications.manage")
      ? [
          { key: "/notifications/templates", icon: <MessageOutlined />, label: t("nav.messageTemplates") },
          { key: "/notifications/log", icon: <SendOutlined />, label: t("nav.deliveryLog") },
        ]
      : []),
    ...(hasPermission("audit.read") ? [{ key: "/security-events", icon: <SafetyOutlined />, label: t("nav.securityLog") }] : []),
  ];

  const groups = [
    { key: "people", label: t("nav.groups.people"), children: people },
    { key: "academic", label: t("nav.groups.academic"), children: academic },
    { key: "finance", label: t("nav.groups.finance"), children: finance },
    { key: "reports", label: t("nav.groups.reports"), children: reports },
    { key: "communication", label: t("nav.groups.communication"), children: communication },
    { key: "administration", label: t("nav.groups.administration"), children: administration },
  ].filter((g) => g.children.length > 0);

  const home: NavItem[] = hasPermission("people.read") ? [{ key: "/", icon: <HomeOutlined />, label: t("nav.home") }] : [];
  const all = [...home, ...groups.flatMap((g) => g.children)];
  const selected = all.find((i) => i.key === "/" ? location.pathname === "/" : location.pathname.startsWith(i.key))?.key ?? all[0]?.key ?? "/";
  const compact = collapsed && !isMobile;

  const menuItems: MenuProps["items"] = [...home, ...groups.map((g) => ({ type: "group" as const, key: g.key, label: compact ? null : g.label, children: g.children }))];

  const menu = (
    <Menu
      theme="dark"
      mode="inline"
      inlineCollapsed={compact}
      selectedKeys={[selected]}
      items={menuItems}
      style={{ borderInlineEnd: "none", paddingBottom: 16 }}
      onClick={({ key }) => {
        navigate(key);
        setDrawerOpen(false);
      }}
    />
  );

  const brandBlock = (
    <div style={{ display: "flex", alignItems: "center", gap: 12, height: 64, padding: compact ? "0 14px" : "0 20px", color: "#fff", flex: "none" }}>
      <img src="/school-logo.jpg" alt="" width={36} height={36} style={{ borderRadius: 8, objectFit: "cover", flex: "none", boxShadow: "0 0 0 1px rgba(255,255,255,0.18)" }} />
      {!compact && (
        <div style={{ lineHeight: 1.25, minWidth: 0 }}>
          <div style={{ fontWeight: 600, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t("app.name")}</div>
          <Typography.Text style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{t("app.portal")}</Typography.Text>
        </div>
      )}
    </div>
  );

  const roleText = (user?.roles ?? []).map((r) => t(`roles.${r}`, { defaultValue: r })).join(", ");
  // The seeded admin is named after its role; do not print the same words twice.
  const roleLabels = roleText === user?.displayName ? "" : roleText;
  const accountMenu: MenuProps = {
    items: [
      {
        key: "who",
        disabled: true,
        label: (
          <div style={{ lineHeight: 1.35, padding: "2px 0", cursor: "default" }}>
            <div style={{ fontWeight: 600, color: brand.ink }}>{user?.displayName}</div>
            {roleLabels && <div style={{ fontSize: 12, color: brand.muted }}>{roleLabels}</div>}
          </div>
        ),
      },
      { type: "divider" },
      { key: "logout", icon: <LogoutOutlined />, label: t("nav.logout"), onClick: () => void logout() },
    ],
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <a className="skip-link" href="#main-content">
        {t("nav.skipToContent")}
      </a>
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          styles={{ body: { padding: 0, background: brand.green }, header: { display: "none" } }}
          size={272}
        >
          {brandBlock}
          {menu}
        </Drawer>
      ) : (
        <Sider
          width={SIDER_WIDTH}
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          collapsed={compact}
          trigger={null}
          style={{ position: "sticky", top: 0, height: "100vh", overflow: "hidden" }}
        >
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {brandBlock}
            <div className="app-sider-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
              {menu}
            </div>
          </div>
        </Sider>
      )}
      <Layout>
        <Header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: isMobile ? "0 12px" : "0 24px",
            borderBottom: `1px solid ${brand.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {isMobile ? (
              <Button type="text" icon={<MenuOutlined />} aria-label={t("nav.menu")} onClick={() => setDrawerOpen(true)} />
            ) : (
              <Tooltip title={collapsed ? t("nav.expand") : t("nav.collapse")} placement="right">
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
                  aria-expanded={!collapsed}
                  onClick={toggleCollapsed}
                />
              </Tooltip>
            )}
          </div>
          <Dropdown menu={accountMenu} trigger={["click"]} placement="bottomRight">
            <Button type="text" aria-label={t("nav.account")} style={{ height: 40, paddingInline: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar size={30} style={{ background: brand.green, fontSize: 13, fontWeight: 600 }}>
                {initials(user?.displayName)}
              </Avatar>
              {!isMobile && (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.2, textAlign: "left" }}>
                  <span style={{ fontWeight: 600, color: brand.ink }}>{user?.displayName}</span>
                  {roleLabels && <span style={{ fontSize: 12, color: brand.muted, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{roleLabels}</span>}
                </span>
              )}
              <DownOutlined style={{ fontSize: 10, color: brand.muted }} />
            </Button>
          </Dropdown>
        </Header>
        <Content id="main-content" tabIndex={-1} style={{ padding: isMobile ? 16 : 28, outline: "none" }}>
          <div style={{ maxWidth: 1440, margin: "0 auto" }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
